import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getErrorMessage } from '../../packages/api-client';
import { useAuth } from '../../packages/auth-client';
import {
    listMyTickets, type CustomerTicket, type TicketPriority, type TicketStatus,
} from '../../packages/api-client/support-api';
import { Badge, PageHeader, StateView } from '../../packages/ui';
import { SupportTicketForm, CATEGORY_LABEL, MY_TICKETS_KEY } from '../../components/SupportTicketForm';
import { usePageTitle } from '../../hooks/usePageTitle';
import { apiDocsUrl } from '../../utils/apiDocsUrl';
import '../settings/Settings.css';
import './Help.css';

const CRUMBS = [{ label: 'Settings', to: '/settings' }, { label: 'Help & support' }];
// Partitioned by user (UI-01) on top of the cache reset at sign-out.
const ticketsKey = (userId?: string) => [...MY_TICKETS_KEY, userId];

const STATUS: Record<TicketStatus, { label: string; variant: 'info' | 'warning' | 'success' | 'neutral' }> = {
    open: { label: 'Open', variant: 'info' },
    in_progress: { label: 'In progress', variant: 'warning' },
    resolved: { label: 'Resolved', variant: 'success' },
    closed: { label: 'Closed', variant: 'neutral' },
};
const PRIORITY: Record<TicketPriority, string> = { low: 'Low', normal: 'Normal', high: 'High' };

const fmt = (iso: string) => new Date(iso).toLocaleString();

function Section({ id, title, intro, children }: { id: string; title: string; intro?: React.ReactNode; children: React.ReactNode }) {
    return (
        <section className="card settings-section" aria-labelledby={id}>
            <h2 id={id} className="settings-section-title">{title}</h2>
            {intro && <p className="settings-section-intro">{intro}</p>}
            {children}
        </section>
    );
}

/**
 * Only the customer-visible fields, named one by one. CustomerTicket has no
 * admin_notes (support-api strips it), and nothing here spreads the ticket.
 */
function TicketDetail({ t, onClose }: { t: CustomerTicket; onClose: () => void }) {
    return (
        <section className="help-ticket-detail" aria-labelledby="help-ticket-detail">
            <div className="help-ticket-detail-head">
                <h3 id="help-ticket-detail">Ticket details</h3>
                <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
            </div>
            <dl className="settings-facts">
                <dt>Subject</dt><dd>{t.subject}</dd>
                <dt>Category</dt><dd>{CATEGORY_LABEL[t.category] ?? t.category}</dd>
                <dt>Status</dt><dd>{STATUS[t.status]?.label ?? t.status}</dd>
                <dt>Priority</dt><dd>{PRIORITY[t.priority] ?? t.priority}</dd>
                <dt>Opened</dt><dd>{fmt(t.created_at)}</dd>
                <dt>Last updated</dt><dd>{fmt(t.updated_at)}</dd>
            </dl>
            <p className="help-ticket-body">{t.body}</p>
            <p className="settings-section-intro">Replies come by email to {t.user_email}.</p>
        </section>
    );
}

function YourTickets() {
    const { user } = useAuth();
    const q = useQuery({ queryKey: ticketsKey(user?.user_id), queryFn: listMyTickets });
    const [openId, setOpenId] = useState<string | null>(null);

    if (q.isPending) return <StateView state="loading" label="Loading tickets" lines={3} />;
    if (q.isError) {
        return <StateView state="error" title="Your tickets couldn't be loaded" message={getErrorMessage(q.error)} onRetry={() => q.refetch()} retrying={q.isFetching} />;
    }
    if (q.data.length === 0) {
        return <StateView state="empty" title="No tickets yet" message="Tickets you send, from this page or the help button, appear here with their status." />;
    }
    const selected = q.data.find((t) => t.id === openId);
    return (
        <>
            <ul className="help-ticket-list">
                {q.data.map((t) => (
                    <li key={t.id}>
                        <button type="button" className="help-ticket-row" aria-expanded={t.id === openId}
                            onClick={() => setOpenId(t.id === openId ? null : t.id)}>
                            <span className="help-ticket-subject">{t.subject}</span>
                            <span className="help-ticket-meta">
                                <Badge variant={STATUS[t.status]?.variant ?? 'neutral'}>{STATUS[t.status]?.label ?? t.status}</Badge>
                                <span>{PRIORITY[t.priority] ?? t.priority} priority</span>
                                <span>{new Date(t.created_at).toLocaleDateString()}</span>
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
            {selected && <TicketDetail t={selected} onClose={() => setOpenId(null)} />}
        </>
    );
}

/** /help — contact support, your tickets, documentation and service status (U27). */
export function HelpPage() {
    usePageTitle('Help & support');
    const docs = apiDocsUrl();
    return (
        <div className="settings-page">
            <PageHeader breadcrumb={CRUMBS} title="Help & support" subtitle="Ask the KaziAtlas team, follow your tickets, and find the docs." />

            <Section id="help-contact" title="Contact support"
                intro="Include any error messages or steps to reproduce if you're reporting a bug.">
                <SupportTicketForm />
            </Section>

            <Section id="help-tickets" title="Your tickets">
                <YourTickets />
            </Section>

            <Section id="help-docs" title="Documentation">
                <ul className="settings-links">
                    <li><Link to="/profile?tab=apikeys">Connect Claude Code and other MCP clients</Link>: API keys with setup steps</li>
                    {docs && <li><a href={docs} target="_blank" rel="noreferrer">API reference</a>: the REST API (OpenAPI)</li>}
                    <li><Link to="/support">Public support page</Link>: email contacts, account and privacy help</li>
                    <li><Link to="/privacy">Privacy policy</Link></li>
                </ul>
            </Section>

            <Section id="help-status" title="Service status">
                <ul className="settings-links">
                    <li><Link to="/health">System status</Link>: whether the API is up right now</li>
                </ul>
            </Section>
        </div>
    );
}
